import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAppSelector } from "../store/hooks";
import { LANGUAGES, translations } from "../translations/landing";
import { logo } from "../assets";
import "./Landing.css";

const THEME_KEY = "lineo-theme";
const LANG_KEY = "lineo-lang";

function getInitialTheme() {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === "light" || saved === "dark") return saved;
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches)
    return "dark";
  return "light";
}

function getInitialLang() {
  const saved = localStorage.getItem(LANG_KEY);
  const validCodes = LANGUAGES.map((l) => l.code);
  return validCodes.includes(saved) ? saved : "tr";
}

export default function Landing() {
  const isLogin = useAppSelector((state) => state.user.isLogin);
  const [theme, setTheme] = useState(getInitialTheme);
  const [lang, setLang] = useState(getInitialLang);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const t = translations[lang] || translations.tr;
  const isRtl = lang === "ar";

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const pageBg = (
    <div
      className="landing-page-bg"
      data-theme={theme}
      aria-hidden="true"
    >
      <div className="hero-light-rays" />
      <div className="hero-glow-orb hero-glow-orb--1" />
      <div className="hero-glow-orb hero-glow-orb--2" />
      <div className="hero-glow-orb hero-glow-orb--3" />
      <div className="hero-shimmer" />
    </div>
  );

  return (
    <>
      {createPortal(pageBg, document.body)}
      <div
        className={`landing ${isRtl ? "landing--rtl" : ""}`}
        data-theme={theme}
        dir={isRtl ? "rtl" : "ltr"}
      >
      <header className="landing-header">
        <Link to="/" className="landing-logo">
          <img src={logo} alt="lineo" className="landing-logo-img" />
        </Link>
        <nav className="landing-nav">
          <div
            className={`lang-selector ${langOpen ? "lang-selector--open" : ""}`}
            ref={langRef}
          >
            <button
              type="button"
              className="lang-trigger"
              onClick={() => setLangOpen((o) => !o)}
              aria-expanded={langOpen}
              aria-haspopup="listbox"
              aria-label="Dil seçin"
            >
              <span className="lang-trigger-flag">{currentLang.flag}</span>
              <span className="lang-trigger-name">{currentLang.name}</span>
              <span className="lang-trigger-arrow">▼</span>
            </button>
            {langOpen && (
              <ul
                className="lang-dropdown"
                role="listbox"
                aria-label="Dil listesi"
              >
                {LANGUAGES.map((l) => (
                  <li key={l.code} role="option">
                    <button
                      type="button"
                      className={`lang-option ${lang === l.code ? "lang-option--active" : ""}`}
                      onClick={() => {
                        setLang(l.code);
                        setLangOpen(false);
                      }}
                    >
                      <span>{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="theme-toggle"
            onClick={toggleTheme}
            title={theme === "light" ? t.themeToggleLight : t.themeToggleDark}
            aria-label={
              theme === "light" ? t.themeToggleLight : t.themeToggleDark
            }
          >
            {theme === "light" ? "🌙" : "☀️"}
          </button>
          {isLogin ? (
            <Link to="/dashboard" className="nav-link nav-link--primary">
              {t.goToPanel}
            </Link>
          ) : (
            <>
              <Link to="/login" className="nav-link">
                {t.login}
              </Link>
              <Link to="/register" className="nav-link nav-link--primary">
                {t.register}
              </Link>
            </>
          )}
        </nav>
      </header>

      <main className="landing-main">
        <section className="hero">
          <h1 className="hero-title">
            {t.heroTitle1}
            <span className="hero-title-accent">{t.heroTitle2}</span>
          </h1>
          <p className="hero-subtitle">{t.heroSubtitle}</p>
          <div className="hero-cta">
            <Link to="/register" className="btn btn--primary">
              {t.getStartedFree}
            </Link>
            <Link to="/login" className="btn btn--secondary">
              {t.login}
            </Link>
          </div>
        </section>

        <section className="features">
          <h2 className="features-title">{t.whylineo}</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🏢</div>
              <h3>{t.feature1Title}</h3>
              <p>{t.feature1Desc}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📋</div>
              <h3>{t.feature2Title}</h3>
              <p>{t.feature2Desc}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>{t.feature3Title}</h3>
              <p>{t.feature3Desc}</p>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>{t.ctaTitle}</h2>
          <p>{t.ctaSubtitle}</p>
          <Link to="/register" className="btn btn--primary btn--large">
            {t.ctaButton}
          </Link>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/privacy-policy">{t.privacyPolicy}</Link>
          <span className="footer-sep">•</span>
          <Link to="/terms-of-use">{t.termsOfService}</Link>
          <span className="footer-sep">•</span>
          <Link to="/cookie-policy">{t.cookiePolicy}</Link>
          <span className="footer-sep">•</span>
          <Link to="/account-deletion">{t.accountDeletion}</Link>
        </div>
        <p>
          © {new Date().getFullYear()} lineo. {t.footerRights}
        </p>
      </footer>
    </div>
    </>
  );
}
